/**
 * D849 (P0-010) - Empresas › **Frotas**: the framework-neutral view-model behind creating a fleet organization, giving it an owner and linking
 * its drivers.
 *
 *  - A fleet is created at the header scope's market (`POST /v1/fleet/organizations`, `fleet:onboard`) and starts in `onboarding`.
 *  - Its owner signs in to the Frota console with a BUSINESS login that holds `FLEET_MANAGER` on that fleet (`POST /v1/admin/operators` then
 *    `POST /v1/admin/operators/:id/grants`, scope FLEET). The login is created when the e-mail has none, reused when it has one - but never a
 *    store (MERCHANT) login, which cannot open the Frota console.
 *  - A driver is linked by a scoped role grant (`POST /v1/fleet/organizations/:id/drivers`, `fleet:manage`). It changes how the driver is
 *    paid, so the screen asks for a confirmation that says so.
 *
 * Finding or creating the owner's login is `resolveLogin` (operators.ts), shared with the store's responsible person.
 * The server authorizes every call; this decides what the console offers and checks the form before the round trip.
 */
import { type Result } from '@vello/core';
import type { FleetOrganizationView, FleetVehicleAssetView, OperatorGrant } from '@vello/api-client';
import type { DomainPermission } from '@vello/rbac';
import { grantsAllow } from './console-access.js';
import type { LoginCandidate } from './operators.js';

export interface FleetAdminActions {
  /** Create a fleet organization (`fleet:onboard`, a platform word). */
  readonly mayCreate: boolean;
  /** Link a driver, read the roster (`fleet:manage`). */
  readonly mayManage: boolean;
  /** Give the fleet an owner: grant `FLEET_MANAGER` (`operator:grant`) and, for a new login, invite it (`operator:manage` at the country). */
  readonly mayInviteOwner: boolean;
}

/** What Empresas › Frotas offers. An unknown authority (`undefined`) offers everything and lets the server refuse. */
export function fleetAdminActions(grants: readonly OperatorGrant[] | undefined): FleetAdminActions {
  if (grants === undefined) return { mayCreate: true, mayManage: true, mayInviteOwner: true };
  const platform = (permission: DomainPermission): boolean => grantsAllow(grants, { permission, target: 'PLATFORM' });
  return {
    mayCreate: platform('fleet:onboard'),
    mayManage: platform('fleet:manage'),
    mayInviteOwner: platform('operator:grant') && grantsAllow(grants, { permission: 'operator:manage', target: 'COUNTRY' }),
  };
}

export type FleetFormIssue = 'name' | 'market' | 'ownerEmail' | 'ownerName' | 'ownerPhone';

export interface NewFleetForm {
  readonly name: string;
  readonly ownerEmail: string;
  readonly ownerName: string;
  readonly ownerPhone: string;
}

export const emptyFleetForm = (): NewFleetForm => ({ name: '', ownerEmail: '', ownerName: '', ownerPhone: '' });

export type { LoginCandidate as FleetOwnerInput } from './operators.js';

export interface NewFleetInput {
  readonly displayName: string;
  readonly scope: { readonly platform: 'vello'; readonly countryCode: string; readonly marketId: string };
  readonly owner?: LoginCandidate;
}

const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,63}$/;
const E164 = /^\+[1-9]\d{6,14}$/;

/** The owner part of the form: all or nothing (an e-mail alone is allowed for a login that exists already; the name and phone then go unused). */
function normalizeOwner(form: NewFleetForm, issues: FleetFormIssue[]): LoginCandidate | undefined {
  const email = form.ownerEmail.trim().toLowerCase();
  if (email === '') return undefined;
  if (!EMAIL.test(email)) issues.push('ownerEmail');
  const name = form.ownerName.trim();
  const phoneE164 = form.ownerPhone.replace(/[\s()-]/g, '');
  if (name.length > 120) issues.push('ownerName');
  if (phoneE164 !== '' && !E164.test(phoneE164)) issues.push('ownerPhone');
  return { email, name, phoneE164 };
}

/** The fleet to create at the market the header scope names, or every problem at once. */
export function normalizeNewFleet(form: NewFleetForm, market: { readonly id: string; readonly countryCode: string } | undefined): Result<NewFleetInput, readonly FleetFormIssue[]> {
  const issues: FleetFormIssue[] = [];
  const displayName = form.name.trim();
  if (displayName === '' || displayName.length > 120) issues.push('name');
  if (market === undefined) issues.push('market');
  const owner = normalizeOwner(form, issues);
  if (issues.length > 0 || market === undefined) return { ok: false, error: issues };
  return { ok: true, value: { displayName, scope: { platform: 'vello', countryCode: market.countryCode, marketId: market.id }, ...(owner !== undefined ? { owner } : {}) } };
}

export interface FleetDriverRow {
  readonly driverId: string;
  /** Vehicles assigned to the driver in this fleet. */
  readonly vehicles: number;
}

/**
 * The fleet's drivers as the Admin can know them: those with a vehicle assigned, plus those linked in this session. The fleet API has no
 * roster read (a link is a scoped grant, read by its id), so a driver linked earlier and not yet given a vehicle is not listed.
 */
export function fleetDriverRows(assets: readonly FleetVehicleAssetView[], linkedNow: readonly string[]): readonly FleetDriverRow[] {
  const counts = new Map<string, number>();
  for (const a of assets) if (a.assignedDriverId !== undefined && a.status !== 'retired') counts.set(a.assignedDriverId, (counts.get(a.assignedDriverId) ?? 0) + 1);
  for (const id of linkedNow) if (!counts.has(id)) counts.set(id, 0);
  return [...counts.entries()].map(([driverId, vehicles]) => ({ driverId, vehicles })).sort((a, b) => a.driverId.localeCompare(b.driverId));
}

/** A fleet that cannot take a driver yet: the server answers `ORG_NOT_ACCEPTING` for these, so the screen says so first. */
export function fleetAcceptsDrivers(org: Pick<FleetOrganizationView, 'status'>): boolean {
  return org.status === 'active' || org.status === 'onboarding';
}
